/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from 'react';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { getTextContrast } from '../../../../utils';
import { NoActiveSubscriptions } from './no-subscriptions';
import { ExpiredSubscriptions } from './expired-subscriptions';
import { ActiveSubscriptions } from './active-subscriptions';
import LoaderOverlay from '../../../molecules/loader-overlay';
import { SignInMessage } from '../../../atoms/sign-in-message';
import { useShowLoaderAndSignInMsg } from '../custom-hooks';
import { NotFoundPage } from '../../not-found';
import { textWithFallback } from '../helpers';
import './styles.m.css';

const FALLBACK_PAGE_TITLE = '<p>Manage Subscriptions</p>';
const FALLBACK_PAGE_DESCRIPTION =
  '<p>View your active and expired subscriptions, and effortlessly switch plans to suit your needs.</p>';
const FALLBACK_ACTIVE_SUB_LABEL = '<p>Active Subscription(s)</p>';

export const ManageSubscriptions = ({ data, target = 'web' }) => {
  const redirectUrl = get(data, ['redirectUrl']);
  const { showLoader, showSignInMessage } = useShowLoaderAndSignInMsg();

  const config = useSelector(state => get(state, ['qt', 'config'], false));

  const enableAccesstype = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'general', 'accesstypeIntegration', 'enableAccesstype'], false)
  );
  const manageSubscriptionsConfig = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'manage-subscriptions', 'rows', '0', 'config'], {})
  );
  const subscriptionConfig = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'subscription', 'pageSetting'], {})
  );
  const localizationConfig = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'general', 'localization'], {})
  );
  const enableDarkMode = useSelector(state => get(state, ['header', 'isDarkModeEnabled'], false));
  const memberSubscriptions = useSelector(state => get(state, ['memberSubscriptions'], []));
  
  if (!enableAccesstype) return <NotFoundPage />;
  if (showSignInMessage) return <SignInMessage />;
  if (showLoader) return <LoaderOverlay />;

  const sketchesHost = get(config, ['sketches-host'], '');

  const activeMemberSubscriptions = memberSubscriptions.filter(
    s => s.status === 'active' && s.subscription_type !== 'dynamic_asset'
  );
  const userHasSubscriptions = activeMemberSubscriptions && activeMemberSubscriptions.length;
  const { buttonColor = '#4860BC', darkButtonColor = '#4860BC' } = subscriptionConfig;
  const mode = enableDarkMode ? 'dark' : 'light';
  const buttonInlineStyles = {
    background: buttonColor,
    color: getTextContrast(buttonColor) === 'dark' ? '#000' : '#fff'
  };
  if (mode === 'dark') {
    buttonInlineStyles.background = darkButtonColor;
    buttonInlineStyles.color = getTextContrast(darkButtonColor) === 'dark' ? '#000' : '#fff';
  }
  const { pageTitle, pageDescription, activeSubscription_label } = manageSubscriptionsConfig;
  const whiteListedUrlParams = get(config, ["pagebuilder-config", "general", "others", "whiteListedUrlParams"], []);
  
  const props = {
    localizationConfig,
    manageSubscriptionsConfig,
    memberSubscriptions,
    mode,
    buttonInlineStyles,
    redirectUrl,
    subscriptionConfig,
    target,
    sketchesHost,
    whiteListedUrlParams
  };

  return (
    <div styleName={`top-lvl-wrapper ${mode}`}>
      <div styleName='page-content'>
        <h1
          styleName='hidden'
          data-test-id='manage-sub-h1'
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageTitle, FALLBACK_PAGE_TITLE) }}
        ></h1>
        <div
          styleName='page-title'
          data-test-id='manage-sub-page-title'
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageTitle, FALLBACK_PAGE_TITLE) }}
        ></div>
        <div
          styleName='page-description'
          data-test-id='manage-sub-page-description'
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageDescription, FALLBACK_PAGE_DESCRIPTION) }}
        ></div>
        <div
          styleName={`active-sub-label ${mode}`}
          dangerouslySetInnerHTML={{ __html: textWithFallback(activeSubscription_label, FALLBACK_ACTIVE_SUB_LABEL) }}
          data-test-id='manage-sub-active-sub-label'
        ></div>
        <div>
          {!!userHasSubscriptions ? <ActiveSubscriptions {...props} /> : <NoActiveSubscriptions {...props} />}
          <ExpiredSubscriptions {...props} />
        </div>
      </div>
    </div>
  );
};
